import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosConfig';
import receptionBg from '../../assets/images/backgrounds/reception-bg.png';
import { SECTION_META_FIELDS, SECTION_REMARKS, REASON_LABEL } from './clearanceTemplate';
import ClearancePrint from './ClearancePrint';

const STATUS_OPTIONS = ['', 'Cleared', 'Pending', 'N/A'];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : '—');
const toInputDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const fmtFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ClearanceDetail = ({ formId, isCoordinator, onBack }) => {
    const [form, setForm] = useState(null);
    const [sections, setSections] = useState([]);     // local editable copy
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [savingKey, setSavingKey] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [editAssigneesFor, setEditAssigneesFor] = useState(null);

    const loadForm = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get(`/api/clearance/${formId}`);
            if (res.data?.success) {
                const f = res.data.data;
                setForm(f);
                setSections(JSON.parse(JSON.stringify(f.sections || [])));
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load clearance form');
        } finally {
            setLoading(false);
        }
    }, [formId]);

    useEffect(() => { loadForm(); }, [loadForm]);

    // Coordinators may need the user list to edit assignees
    useEffect(() => {
        if (!isCoordinator) return;
        axiosInstance.get('/api/clearance/users')
            .then(res => { if (res.data?.success) setAllUsers(res.data.data || []); })
            .catch(() => {});
    }, [isCoordinator]);

    const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 2500); };

    const updateSection = (idx, mutator) => {
        setSections(prev => {
            const next = [...prev];
            const copy = JSON.parse(JSON.stringify(next[idx]));
            mutator(copy);
            next[idx] = copy;
            return next;
        });
    };

    const setLineItem = (sIdx, liIdx, field, value) =>
        updateSection(sIdx, (s) => { s.lineItems[liIdx][field] = value; });

    const setMeta = (sIdx, key, value) =>
        updateSection(sIdx, (s) => { s.meta = { ...(s.meta || {}), [key]: value }; });

    const setSectionDate = (sIdx, value) =>
        updateSection(sIdx, (s) => { s.date = value || null; });

    /**
     * Save a section. With `complete = true` the server digitally signs it with
     * the logged-in user's name and locks it — there is no drawn signature.
     */
    const saveSection = async (sIdx, complete = false) => {
        const section = sections[sIdx];
        if (complete && !window.confirm(
            `Complete "${section.title}"?\n\nIt will be digitally signed in your name and locked. ` +
            `Only a coordinator can reopen it.`
        )) return;

        setSavingKey(section.key);
        setError('');
        try {
            const payload = {
                lineItems: section.lineItems.map(li => ({
                    description: li.description,
                    status: li.status,
                    remarks: li.remarks
                })),
                meta: section.meta || {},
                date: section.date || null,
                complete
            };

            const res = await axiosInstance.patch(`/api/clearance/${formId}/sections/${section.key}`, payload);
            if (res.data?.success) {
                flash(res.data.message || `${section.title} saved`);
                await loadForm();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save section');
        } finally {
            setSavingKey(null);
        }
    };

    const reopenSection = async (sectionKey) => {
        if (!window.confirm('Reopen this section? Its digital signature will be cleared and it must be completed again.')) return;
        try {
            const res = await axiosInstance.patch(`/api/clearance/${formId}/sections/${sectionKey}/reopen`);
            if (res.data?.success) { flash('Section reopened'); await loadForm(); }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reopen section');
        }
    };

    const saveAssignees = async (sectionKey, assigneeIds) => {
        try {
            const res = await axiosInstance.patch(
                `/api/clearance/${formId}/sections/${sectionKey}/assignees`,
                { assignees: assigneeIds }
            );
            if (res.data?.success) { setEditAssigneesFor(null); flash('Assignees updated'); await loadForm(); }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update assignees');
        }
    };

    const cancelForm = async () => {
        const reason = window.prompt('Reason for cancelling this clearance form?');
        if (reason === null) return;
        try {
            const res = await axiosInstance.patch(`/api/clearance/${formId}/cancel`, { cancelReason: reason });
            if (res.data?.success) { flash('Form cancelled'); await loadForm(); }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to cancel form');
        }
    };

    if (loading) {
        return (
            <div className="clearance-container" style={{ backgroundImage: `url(${receptionBg})` }}>
                <div className="clearance-content"><div className="clearance-empty">Loading…</div></div>
            </div>
        );
    }

    if (!form) {
        return (
            <div className="clearance-container" style={{ backgroundImage: `url(${receptionBg})` }}>
                <div className="clearance-content">
                    <div className="clearance-error">{error || 'Form not found'}</div>
                    <button className="btn btn-secondary" onClick={onBack}>← Back</button>
                </div>
            </div>
        );
    }

    if (printing) {
        return <ClearancePrint form={{ ...form, sections }} onDone={() => setPrinting(false)} />;
    }

    const emp = form.employee || {};
    const completedCount = sections.filter(s => s.status === 'completed').length;

    return (
        <div className="clearance-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="clearance-content">
                <div className="page-header">
                    <img src="/logo.png" alt="Logo" className="app-logo" />
                    <div className="page-header-text">
                        <h1>Clearance — {emp.name}</h1>
                        <p>
                            {form.clearanceId && (
                                <><span className="clearance-id-badge">{form.clearanceId}</span>{' · '}</>
                            )}
                            {REASON_LABEL[form.clearance?.reason] || form.clearance?.reason}
                            {form.clearance?.reason === 'other' && form.clearance?.reasonOther
                                ? `: ${form.clearance.reasonOther}` : ''}
                            {' · '}{completedCount}/{sections.length} sections completed
                        </p>
                    </div>
                    <div className="page-header-actions">
                        <div className="page-header-buttons">
                            <button className="btn btn-secondary" onClick={() => setPrinting(true)}>🖨 Print</button>
                            {isCoordinator && form.status !== 'cancelled' && (
                                <button className="btn btn-danger" onClick={cancelForm}>Cancel Form</button>
                            )}
                        </div>
                        <button onClick={onBack} className="btn-back">← Back</button>
                    </div>
                </div>

                {notice && <div className="clearance-notice">{notice}</div>}
                {error && <div className="clearance-error">{error}</div>}
                {form.status === 'cancelled' && (
                    <div className="clearance-cancelled-banner">
                        This form was cancelled by {form.cancelledBy}
                        {form.cancelReason ? ` — ${form.cancelReason}` : ''}.
                    </div>
                )}
                {form.status === 'cleared' && (
                    <div className="clearance-cleared-banner">✓ All sections completed — employee cleared.</div>
                )}

                {/* Employee header card */}
                <div className="card clearance-emp-card">
                    <div className="emp-grid">
                        <div><label>Staff Name</label><span>{emp.name || '—'}</span></div>
                        <div><label>Staff Code</label><span>{emp.staffCode || '—'}</span></div>
                        <div><label>Designation</label><span>{emp.designation || '—'}</span></div>
                        <div><label>Reporting To</label><span>{emp.reportingTo || '—'}</span></div>
                        <div><label>Branch</label><span>{emp.branch || '—'}</span></div>
                        <div><label>Staff QID</label><span>{emp.qid || '—'}</span></div>
                        <div><label>QID Expiry</label><span>{fmtDate(emp.qidExpiry)}</span></div>
                        <div><label>Date</label><span>{fmtDate(emp.date)}</span></div>
                        <div><label>Travel Date</label><span>{fmtDate(form.clearance?.travelDate)}</span></div>
                    </div>
                </div>

                {/* Sections */}
                {sections.map((section, sIdx) => {
                    const metaFields = SECTION_META_FIELDS[section.key] || [];
                    const editable = section.canEdit; // server-computed for this user
                    return (
                        <div className={`card clearance-section ${section.status}`} key={section.key}>
                            <div className="section-head">
                                <div className="section-title-wrap">
                                    <h3>{section.title}</h3>
                                    <span className={`status-pill status-section-${section.status}`}>
                                        {section.status === 'completed' ? 'Completed' : 'Pending'}
                                    </span>
                                </div>
                                <div className="section-assignees">
                                    <span className="assignees-label">Assigned:</span>
                                    {(section.assignees || []).length === 0 ? (
                                        <em>none</em>
                                    ) : (
                                        section.assignees.map(a => (
                                            <span className="assignee-chip small" key={a._id || a}>
                                                {a.fullName || a.username || 'User'}
                                            </span>
                                        ))
                                    )}
                                    {isCoordinator && section.status !== 'completed' && form.status !== 'cancelled' && (
                                        <button className="btn-link" onClick={() => setEditAssigneesFor(section.key)}>
                                            edit
                                        </button>
                                    )}
                                    {editAssigneesFor === section.key && (
                                        <EditAssigneesPopover
                                            users={allUsers}
                                            current={(section.assignees || []).map(a => String(a._id || a))}
                                            onCancel={() => setEditAssigneesFor(null)}
                                            onSave={(ids) => saveAssignees(section.key, ids)}
                                        />
                                    )}
                                </div>
                            </div>

                            {!editable && section.status !== 'completed' && form.status !== 'cancelled' && (
                                <div className="section-readonly-hint">
                                    You are not assigned to this section — read only.
                                </div>
                            )}

                            {/* Extra (meta) fields + section date */}
                            <div className="section-meta">
                                {metaFields.map(mf => (
                                    <label key={mf.key} className="meta-field">
                                        <span>{mf.label}</span>
                                        {mf.type === 'yesno' ? (
                                            <select
                                                value={(section.meta && section.meta[mf.key]) || ''}
                                                disabled={!editable}
                                                onChange={(e) => setMeta(sIdx, mf.key, e.target.value)}
                                            >
                                                <option value="">—</option>
                                                <option value="Yes">Yes</option>
                                                <option value="No">No</option>
                                            </select>
                                        ) : mf.type === 'date' ? (
                                            <input
                                                type="date"
                                                value={toInputDate(section.meta && section.meta[mf.key])}
                                                disabled={!editable}
                                                onChange={(e) => setMeta(sIdx, mf.key, e.target.value)}
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                value={(section.meta && section.meta[mf.key]) || ''}
                                                disabled={!editable}
                                                onChange={(e) => setMeta(sIdx, mf.key, e.target.value)}
                                            />
                                        )}
                                    </label>
                                ))}
                                <label className="meta-field">
                                    <span>Date</span>
                                    <input
                                        type="date"
                                        value={toInputDate(section.date)}
                                        disabled={!editable}
                                        onChange={(e) => setSectionDate(sIdx, e.target.value)}
                                    />
                                </label>
                            </div>

                            {/* Line items */}
                            <table className="section-table">
                                <thead>
                                    <tr>
                                        <th className="col-no">No.</th>
                                        <th className="col-part">Particulars</th>
                                        <th className="col-desc">Description</th>
                                        <th className="col-status">Status</th>
                                        <th className="col-remarks">Remarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {section.lineItems.map((li, liIdx) => (
                                        <tr key={liIdx}>
                                            <td className="col-no">{li.particulars ? liIdx + 1 : ''}</td>
                                            <td className="col-part">{li.particulars}</td>
                                            <td className="col-desc">
                                                <input
                                                    type="text"
                                                    value={li.description}
                                                    disabled={!editable}
                                                    onChange={(e) => setLineItem(sIdx, liIdx, 'description', e.target.value)}
                                                />
                                            </td>
                                            <td className="col-status">
                                                <select
                                                    value={li.status}
                                                    disabled={!editable}
                                                    onChange={(e) => setLineItem(sIdx, liIdx, 'status', e.target.value)}
                                                >
                                                    {STATUS_OPTIONS.map(o => (
                                                        <option key={o} value={o}>{o || '—'}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="col-remarks">
                                                <input
                                                    type="text"
                                                    value={li.remarks}
                                                    disabled={!editable}
                                                    onChange={(e) => setLineItem(sIdx, liIdx, 'remarks', e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Supporting files for this department clearance */}
                            <SectionAttachments
                                formId={formId}
                                section={section}
                                editable={editable}
                                onChanged={loadForm}
                                onError={setError}
                                flash={flash}
                            />

                            {/* Section-level remarks (stored in meta.remarks) */}
                            {SECTION_REMARKS.includes(section.key) && (
                                <label className="section-remarks">
                                    <span>Remarks / Comments</span>
                                    <textarea
                                        rows={3}
                                        value={(section.meta && section.meta.remarks) || ''}
                                        disabled={!editable}
                                        placeholder="Any additional notes for this section…"
                                        onChange={(e) => setMeta(sIdx, 'remarks', e.target.value)}
                                    />
                                </label>
                            )}

                            {/* Digital signature — stamped by the server, never drawn */}
                            {section.signedByName && (
                                <div className="section-signed">
                                    <span className="signed-icon">✓</span>
                                    <div className="signed-text">
                                        <strong>Digitally signed by {section.signedByName}</strong>
                                        <span>{fmtDateTime(section.signedAt)}</span>
                                    </div>
                                </div>
                            )}

                            {/* Section actions */}
                            <div className="section-actions">
                                {section.status === 'completed' ? (
                                    <>
                                        <span className="section-locked">🔒 Locked</span>
                                        {isCoordinator && form.status !== 'cancelled' && (
                                            <button className="btn btn-secondary" onClick={() => reopenSection(section.key)}>
                                                Reopen
                                            </button>
                                        )}
                                    </>
                                ) : editable ? (
                                    <>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => saveSection(sIdx, false)}
                                            disabled={savingKey === section.key}
                                        >
                                            {savingKey === section.key ? 'Saving…' : 'Save Draft'}
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => saveSection(sIdx, true)}
                                            disabled={savingKey === section.key}
                                        >
                                            Complete &amp; Sign
                                        </button>
                                        <span className="section-sign-hint">
                                            Completing signs this section in your name.
                                        </span>
                                    </>
                                ) : null}
                            </div>

                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/**
 * Attachments for one department clearance section. Files are stored on the HR
 * SharePoint site (or local disk, per CLEARANCE_ATTACHMENTS_STORAGE) and always
 * streamed back through the card-protected API — never a raw SharePoint link.
 */
const SectionAttachments = ({ formId, section, editable, onChanged, onError, flash }) => {
    const [busy, setBusy] = useState(false);
    const [picked, setPicked] = useState([]);
    const inputRef = React.useRef(null);
    const docs = section.documents || [];
    const base = `/api/clearance/${formId}/sections/${section.key}/documents`;

    const view = async (doc) => {
        try {
            const res = await axiosInstance.get(`${base}/${doc._id}/content`, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: res.data.type || doc.fileType || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (err) {
            onError(err.response?.data?.message || 'Failed to open attachment');
        }
    };

    const upload = async () => {
        if (!picked.length) return;
        setBusy(true);
        onError('');
        try {
            const fd = new FormData();
            picked.forEach((f) => fd.append('files', f));
            const res = await axiosInstance.post(base, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data?.success) {
                flash(res.data.message || 'Files uploaded');
                setPicked([]);
                if (inputRef.current) inputRef.current.value = '';
                await onChanged();
            }
        } catch (err) {
            onError(err.response?.data?.message || 'Failed to upload files');
        } finally {
            setBusy(false);
        }
    };

    const remove = async (doc) => {
        if (!window.confirm(`Remove "${doc.fileName}"?`)) return;
        setBusy(true);
        onError('');
        try {
            const res = await axiosInstance.delete(`${base}/${doc._id}`);
            if (res.data?.success) { flash('Attachment removed'); await onChanged(); }
        } catch (err) {
            onError(err.response?.data?.message || 'Failed to remove attachment');
        } finally {
            setBusy(false);
        }
    };

    if (!editable && docs.length === 0) return null;

    return (
        <div className="section-attachments">
            <div className="section-attachments-head">Attachments</div>
            {docs.length === 0 ? (
                <div className="section-attachments-empty">No files attached.</div>
            ) : (
                <ul className="section-attachments-list">
                    {docs.map((doc) => (
                        <li key={doc._id}>
                            <button type="button" className="btn-link" onClick={() => view(doc)}>
                                📎 {doc.fileName}
                            </button>
                            {doc.fileSize ? <span className="att-size">{fmtFileSize(doc.fileSize)}</span> : null}
                            <span className="att-by">
                                {doc.uploadedBy ? `by ${doc.uploadedBy}` : ''} {fmtDate(doc.uploadedAt)}
                            </span>
                            {editable && (
                                <button
                                    type="button"
                                    className="btn-link att-remove"
                                    disabled={busy}
                                    onClick={() => remove(doc)}
                                >
                                    remove
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {editable && (
                <div className="section-attachments-upload">
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                        disabled={busy}
                        onChange={(e) => setPicked(Array.from(e.target.files || []))}
                    />
                    <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={busy || picked.length === 0}
                        onClick={upload}
                    >
                        {busy ? 'Uploading…' : `Upload${picked.length ? ` (${picked.length})` : ''}`}
                    </button>
                </div>
            )}
        </div>
    );
};

/** Small inline popover to change a section's assignees. */
const EditAssigneesPopover = ({ users, current, onCancel, onSave }) => {
    const [selected, setSelected] = useState(current);
    const toggle = (id) => setSelected(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
    return (
        <div className="assignees-popover">
            <div className="assignees-popover-list">
                {users.map(u => (
                    <label key={u._id} className="assignee-option">
                        <input
                            type="checkbox"
                            checked={selected.includes(String(u._id))}
                            onChange={() => toggle(String(u._id))}
                        />
                        <span>{u.fullName}</span>
                        {u.department && <span className="assignee-dept">{u.department}</span>}
                    </label>
                ))}
            </div>
            <div className="assignees-popover-actions">
                <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                <button className="btn btn-primary" onClick={() => onSave(selected)}>Save</button>
            </div>
        </div>
    );
};

export default ClearanceDetail;
